/**
 * @fileOverview Synthetic input inside the frame — a mouse press, typed characters, Escape.
 *
 * Every event is built with the **frame's own** constructors: the elements live in the frame's realm,
 * and an event from the host's realm is a foreign object to them. None of these events is `isTrusted`,
 * which is what lets `LewViewHelpDialog` tell them apart from the reader's own keys.
 */

import type { Point } from "@/services/viewHelp/pointer"

/** The frame's constructors. The DOM typings put a realm's constructors on `typeof globalThis`, not on `Window`. */
const realm = (win: Window) => win as Window & typeof globalThis

/**
 * The mouse events the pointer sends. A press, a release, the travel in between, and a right-click —
 * and a click carrying its coordinates, for a map, which reads where it was clicked off the event.
 */
export type MouseKind = "mousedown" | "mouseup" | "click" | "mousemove" | "mouseover" | "mouseout" | "contextmenu"

/** The HTML5 drag events, for a source the browser itself would carry rather than a mouse-driven one. */
export type DragKind = "dragstart" | "dragenter" | "dragover" | "dragleave" | "drop" | "dragend"

/**
 * Presses or releases a real element at a point, so it reacts the way it would to a mouse.
 *
 * Vuetify's ripple listens for `mousedown` and `mouseup` and reads the event's coordinates, so a tab
 * ripples from exactly where the pointer landed — the control visibly taking the click, rather than
 * a ring drawn over it.
 *
 * `init` carries what a particular event needs on top of that: `button: 2` for a right-click,
 * `buttons: 1` for a move made with the button held, `relatedTarget` for the pair of `mouseout` and
 * `mouseover` that a pointer crossing from one element to the next produces.
 */
/** The parts of a mouse event that differ between a press, a right-click and a move under a held button. */
interface MouseDetail {
  /** Which button the event is about. 0 is the left one, 2 the right. */
  button?: number
  /** Which buttons are down while it happens — 1 during a drag, 0 once it is released. */
  buttons?: number
  /** The element the pointer came from or went to, on the `mouseout` / `mouseover` pair. */
  relatedTarget?: EventTarget | null
  /** Held down with the press. A grid that reads `event.ctrlKey` cannot be worked without them. */
  ctrlKey?: boolean
  shiftKey?: boolean
}

export function dispatchMouse(win: Window, el: HTMLElement, type: MouseKind, at: Point, init: MouseDetail = {}) {
  el.dispatchEvent(
    new (realm(win).MouseEvent)(type, {
      bubbles: true,
      cancelable: true,
      clientX: at.x,
      clientY: at.y,
      button: 0,
      view: win,
      ...init,
    })
  )
}

/**
 * A clipboard for one drag, made in the frame's realm.
 *
 * An HTML5 drag carries its payload on the event, and the handlers that receive it read it back with
 * `getData` — the planning board's marked days travel in there to the project row they are dropped
 * on. A dispatched `DragEvent` gets no `dataTransfer` of its own, so the drag has to bring one and
 * hand the same object to every event of the sequence.
 */
export const newDataTransfer = (win: Window) => new (realm(win).DataTransfer)()

/** One event of an HTML5 drag, at a point, carrying the drag's own {@link newDataTransfer}. */
export function dispatchDrag(win: Window, el: HTMLElement, type: DragKind, at: Point, dataTransfer: DataTransfer) {
  el.dispatchEvent(
    new (realm(win).DragEvent)(type, {
      bubbles: true,
      cancelable: true,
      clientX: at.x,
      clientY: at.y,
      button: 0,
      view: win,
      dataTransfer,
    })
  )
}

/**
 * Sets a field's value and fires a genuine `input` event, which is all a Vuetify field or a `v-model`
 * reads — the field formats and validates it exactly as it would a keyboard.
 */
export function writeInput(win: Window, field: HTMLInputElement, value: string, data: string) {
  field.value = value
  field.dispatchEvent(new (realm(win).InputEvent)("input", { bubbles: true, inputType: "insertText", data }))
}

/**
 * Presses Escape inside the frame — Vuetify's overlays hear it on their own window.
 *
 * The personnel planning board's context menu is not a Vuetify overlay: it is plain DOM teleported to
 * the body, and it listens for a click outside itself rather than for a key. So it is dismissed here
 * too, by a click on the full-screen layer it draws for exactly that purpose. Without it, "close
 * whatever is on top" would have been true of every menu in the product but that one — and a scene
 * that right-clicked a span would have left its menu standing over the next step.
 */
export function pressEscape(win: Window) {
  // The planning board's context menu is closed by its overlay alone. Escape on the board also clears
  // the marked days — the very selection the next drag carries — while leaving that menu open, so the
  // assign step's own Escape threw away what it was about to drag.
  const overlay = win.document.querySelector<HTMLElement>(".context-menu-overlay")
  if (overlay) {
    overlay.click()
    return
  }
  const target = win.document.activeElement ?? win.document.body
  target.dispatchEvent(
    new (realm(win).KeyboardEvent)("keydown", { key: "Escape", code: "Escape", bubbles: true, cancelable: true })
  )
}
